// #29 Tabs: 40 px tabs in --text-2 that turn --text on hover; the active tab is --text, 600, over a 2 px red
// underline. A tab may carry a Count or a state Pill (P-8, D-12); a count is read after a visually hidden ", ", as the
// navigation badge is, so the tab is named "Due, 1", never "Due1". Two forms:
//  - route tabs (`route`): each tab is a link to its own screen, in a navigation landmark; the active one is
//    aria-current="page";
//  - in-page tabs: a tablist of role="tab" buttons, one tab stop (roving tabindex: arrows with wrap, Home, End), the
//    selection follows focus, each tab controls its tabpanel (inactive panels stay in the DOM, hidden).
// An item with can={false} is absent.

import { useState, type ReactNode } from "react";
import { Count } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useRoving } from "../lib/useRoving";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";

export interface TabsItem {
  key: string;
  /** The tab's name (his words: *Prioritized actions · History*, *Compensation · Offer* …). */
  label: string;
  /** A count beside the name. */
  count?: number | undefined;
  /** A state pill beside the name. */
  pill?: ReactNode | undefined;
  /** Route tabs: the screen the tab leads to. */
  href?: string | undefined;
  /** In-page tabs: the panel the tab shows. */
  panel?: ReactNode | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface TabsProps {
  /** Names the tablist or the navigation landmark. */
  label: string;
  items: readonly TabsItem[];
  /** The active tab's key (controlled). */
  active?: string | undefined;
  /** The first active tab (uncontrolled; default the first item). */
  defaultActive?: string | undefined;
  onSelect?: ((key: string) => void) | undefined;
  /** Route tabs: links, the active one aria-current="page". */
  route?: boolean | undefined;
  className?: string | undefined;
}

function TabInner({ item }: { item: TabsItem }) {
  return (
    <>
      <span className="mc-tab-t">{item.label}</span>
      {item.count !== undefined ? <><VisuallyHidden>, </VisuallyHidden><Count n={item.count} /></> : null}
      {item.pill ?? null}
    </>
  );
}

function RouteTab({ item, active }: { item: TabsItem; active: boolean }) {
  const hot = useHs(item.hs);
  return (
    <a className={cx("mc-tab", active && "mc-on")} href={item.href} aria-current={active ? "page" : undefined} {...hot}>
      <TabInner item={item} />
    </a>
  );
}

function PageTab({ item, active, id, panel, roving, index, select }: {
  item: TabsItem;
  active: boolean;
  id: string;
  panel: string;
  roving: ReturnType<typeof useRoving>;
  index: number;
  select: (key: string) => void;
}) {
  const hot = useHs(item.hs);
  return (
    <button
      {...roving.item(index)}
      type="button"
      role="tab"
      id={id}
      className={cx("mc-tab", active && "mc-on")}
      aria-selected={active}
      aria-controls={panel}
      onClick={() => { select(item.key); roving.focus(index); }}
      {...hot}
    >
      <TabInner item={item} />
    </button>
  );
}

export function Tabs({ label, items, active, defaultActive, onSelect, route, className }: TabsProps) {
  const shown = items.filter((item) => item.can !== false);
  const base = useSvgId();
  const [own, setOwn] = useState(defaultActive ?? shown[0]?.key);
  const current = active ?? own;
  const index = Math.max(0, shown.findIndex((item) => item.key === current));
  const select = (key: string) => {
    if (active === undefined) setOwn(key);
    onSelect?.(key);
  };
  const roving = useRoving({
    count: shown.length,
    active: index,
    orientation: "horizontal",
    selectOnFocus: true,
    onSelect: (to) => { const item = shown[to]; if (item) select(item.key); },
  });

  if (route) {
    return (
      <nav className={cx("mc-tabs", className)} aria-label={label}>
        {shown.map((item) => <RouteTab key={item.key} item={item} active={item.key === current} />)}
      </nav>
    );
  }
  const safe = (key: string) => key.replace(/[^a-zA-Z0-9_-]/g, "");
  const tabId = (key: string) => `${base}-tab-${safe(key)}`;
  const panelId = (key: string) => `${base}-panel-${safe(key)}`;
  return (
    <div className={cx("mc-tabset", className)}>
      <div className="mc-tabs" role="tablist" aria-label={label} aria-orientation="horizontal" {...roving.container}>
        {shown.map((item, at) => (
          <PageTab key={item.key} item={item} active={at === index} id={tabId(item.key)} panel={panelId(item.key)} roving={roving} index={at} select={select} />
        ))}
      </div>
      {shown.map((item, at) => (
        <div key={item.key} role="tabpanel" id={panelId(item.key)} aria-labelledby={tabId(item.key)} className="mc-tabpanel" hidden={at !== index} tabIndex={0}>
          {item.panel ?? null}
        </div>
      ))}
    </div>
  );
}
